import { useEffect, useState } from 'react';
import type {
  PreScreenAnswers,
  PreScreenQuestion,
  PreScreenQuestionType,
} from '../../../../utils/types/assessment_types';
import type { ILocationData } from '../interfaces';

export type QualifyingAnswer = PreScreenAnswers & {
  videoFile?: File;
  previewUrl?: string;
  locationData?: ILocationData;
};

export interface IUseQualifyingAnswers {
  answers: QualifyingAnswer[];
  handleChangeAnswer: (
    questionId: string,
    questionType: PreScreenQuestionType,
    answer: string | number,
    locationData?: ILocationData,
  ) => void;
  handleVideoCapture: (questionId: string, file: File, previewUrl: string) => void;
  getAnswerValue: (questionId: string) => string | number;
  currentQuestion?: PreScreenQuestion;
}

interface IUseQualifyingAnswersParams {
  questions: PreScreenQuestion[];
  currentStep: number;
  onComplete: (answers: PreScreenAnswers[]) => void;
}

export const useQualifyingAnswers = ({
  questions,
  currentStep,
  onComplete,
}: IUseQualifyingAnswersParams): IUseQualifyingAnswers => {
  const [answers, setAnswers] = useState<QualifyingAnswer[]>([]);

  useEffect(() => {
    onComplete(answers);
  }, [answers, onComplete]);

  const handleChangeAnswer = (
    questionId: string,
    questionType: PreScreenQuestionType,
    answer: string | number,
    locationData?: ILocationData,
  ): void => {
    const transformedAnswer: string | number =
      questionType === 'numeric' ? (answer === '' ? '' : parseInt(answer as string, 10)) : answer.toString();

    setAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex((ans) => ans.questionId === questionId);
      const entry: QualifyingAnswer = {
        questionId,
        answer: transformedAnswer,
        questionType,
        locationData,
      };
      if (existingAnswerIndex !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = entry;
        return updatedAnswers;
      }
      return [...prevAnswers, entry];
    });
  };

  const handleVideoCapture = (questionId: string, file: File, previewUrl: string): void => {
    setAnswers((prevAnswers) => {
      const existingIndex = prevAnswers.findIndex((ans) => ans.questionId === questionId);
      const entry: QualifyingAnswer = {
        questionId,
        answer: previewUrl,
        questionType: 'video',
        videoFile: file,
        previewUrl,
      };
      if (existingIndex !== -1) {
        const updated = [...prevAnswers];
        updated[existingIndex] = entry;
        return updated;
      }
      return [...prevAnswers, entry];
    });
  };

  const getAnswerValue = (questionId: string): string | number => {
    const answerObj = answers.find((ans) => ans.questionId === questionId);
    return answerObj ? answerObj.answer : '';
  };

  return {
    answers,
    handleChangeAnswer,
    handleVideoCapture,
    getAnswerValue,
    currentQuestion: questions[currentStep],
  };
};
